import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";

import { useFixedActionButtons } from "../FixedActions/FixedActionButtonsContext";
import { closest } from "./DomUtils";
import { useGetStickyElementsHeight } from "./StickyElementsHeight";

const FOCUS_VISIBILITY_SCROLL_OFFSET = 40;

export const useFocusVisibilityManager = () => {
  const { buttonsContainer } = useFixedActionButtons();
  const buttonsContainerHeight = buttonsContainer?.offsetHeight || 0;

  // Use the function as we only need to get the values of the sticky elements height
  // in the "focusin" event callback, and not on every render. This avoids useless
  // re-renders.
  const getStickyElementsHeight = useGetStickyElementsHeight();

  const hasActiveModals = useSelector(
    ({ modals }) => Object.keys(modals.activeModals).length > 0
  );

  const handleFocusIn = useCallback(
    e => {
      // Don't move window scroll if a modal is active as the actual scrollable container
      // is not the window, and this changes the background page scroll, and that's not
      // we want. Also, modals don't have masking fixed elements, so we can just let the
      // browser handle the scrolling natively.
      if (hasActiveModals) return;

      // Do not scroll the window to sticky element child items as they're already in the
      // viewport as they're inside a fixed position container. We also check that the
      // target is not a .modal-portal child, as the `hasActiveModals` dependency may not
      // be updated on the first "focusin" event when the modal is opened. Additionally,
      // tabpanel containers are ignored to prevent unwanted scrolling when clicking on
      // empty areas within forms, as tabpanels have tabIndex="0" for accessibility but
      // shouldn't trigger scroll behavior since they're large content containers.
      //
      // We ensure that the className is a string as SVG elements' className is an
      // SVGAnimatedString object, and that we don't want to match on it.
      const isStickyElementChildFocus = closest(e.target, el => {
        const hasStringClassName = typeof el.className === "string";

        const hasStickyClassName =
          hasStringClassName &&
          el.className.match(/title-bar-sticky|fixed-button-actions|modal-portal/);

        const hasTabpanelRole =
          typeof el.getAttribute === "function" && el.getAttribute("role") === "tabpanel";

        return hasStickyClassName || hasTabpanelRole;
      });

      if (isStickyElementChildFocus) return;

      const elementBounds = e.target.getBoundingClientRect();
      const elementY = elementBounds.y;
      const { scrollY } = window;
      const minimumScrollYTopOffset =
        getStickyElementsHeight() + FOCUS_VISIBILITY_SCROLL_OFFSET;

      // If the top of the focused element isn't visibile after the default browser
      // focus scrolling, because of sticky top elements, we scroll the page ourselves
      // to ensure that the focused element becomes visible.
      if (elementY < minimumScrollYTopOffset) {
        return window.scrollTo({ top: scrollY - (minimumScrollYTopOffset - elementY) });
      }

      const elementBottom = window.innerHeight - elementBounds.bottom;
      // Since the `buttonsContainer` only appear on mobile, and may not be present on
      // some pages, we only give a value to `minimumScrollYBottomOffset` when they are
      // present on the page and have a height other than 0.
      const minimumScrollYBottomOffset =
        buttonsContainerHeight && buttonsContainerHeight + FOCUS_VISIBILITY_SCROLL_OFFSET;
      const elementHeightOverflowsWindow =
        elementBounds.height > minimumScrollYBottomOffset - minimumScrollYTopOffset;

      // If the element is smaller than the visible window height, and that it doesn't
      // appear because it's hidden behind the sticky buttons container, we scroll the
      // page to make them visible.
      if (!elementHeightOverflowsWindow && elementBottom < minimumScrollYBottomOffset) {
        window.scrollTo({ top: scrollY + (minimumScrollYBottomOffset - elementBottom) });
      }
    },
    [hasActiveModals, getStickyElementsHeight, buttonsContainerHeight]
  );

  useEffect(() => {
    // Do not bind the "focusin" event if we're inside Capybara, to avoid issues when
    // clicking on some links that generate a useless scroll, as this features is only
    // meant to help users that browse the app with they keyboard.
    if (window.CAPYBARA_ENV) return;

    window.addEventListener("focusin", handleFocusIn);

    return () => window.removeEventListener("focusin", handleFocusIn);
  }, [handleFocusIn]);
};
